import React, { useState, useEffect } from 'react';

const StatisticsSection = ({ colors, visibleSections }) => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawResponse, setRawResponse] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        console.log('Fetching statistics...');
        const response = await fetch('https://api.dremerz.net/api/DZ_8923_Idlbay_Stat_StatList');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('===== FULL STATS API RESPONSE =====');
        console.log('Raw data:', JSON.stringify(data, null, 2));
        console.log('Type of data:', typeof data);
        console.log('Is Array:', Array.isArray(data));
        console.log('Keys:', Object.keys(data));
        
        setRawResponse(data); // Save for debugging display
        
        // More robust data extraction with multiple fallbacks
        let statsData = [];
        
        if (Array.isArray(data)) {
          console.log('Data is direct array');
          statsData = data;
        } else if (data && Array.isArray(data.data)) {
          console.log('Data is in data.data array');
          statsData = data.data;
        } else if (data && Array.isArray(data.stats)) {
          console.log('Data is in data.stats array');
          statsData = data.stats;
        } else if (data && Array.isArray(data.result)) {
          console.log('Data is in data.result array');
          statsData = data.result;
        } else if (data && Array.isArray(data.records)) {
          console.log('Data is in data.records array');
          statsData = data.records;
        } else if (data && typeof data.data === 'object' && data.data !== null) {
          console.log('Data.data is object, converting to array');
          statsData = Object.values(data.data);
        } else if (data && typeof data === 'object') {
          console.log('Searching for any array in data object');
          // Try to find any array in the response
          const possibleArrays = Object.entries(data)
            .filter(([key, val]) => Array.isArray(val))
            .map(([key, val]) => ({ key, val }));
          
          console.log('Found arrays:', possibleArrays.map(p => `${p.key}: ${p.val.length} items`));
          
          if (possibleArrays.length > 0) {
            statsData = possibleArrays[0].val;
            console.log('Using first array found:', possibleArrays[0].key);
          }
        }
        
        console.log('===== PROCESSED STATS DATA =====');
        console.log('Processed stats:', statsData);
        console.log('Number of stats:', statsData.length);
        if (statsData.length > 0) {
          console.log('First stat item:', statsData[0]);
          console.log('First stat keys:', Object.keys(statsData[0]));
        }
        
        setStats(statsData);
      } catch (err) {
        console.error('===== ERROR FETCHING STATISTICS =====');
        console.error('Error details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  console.log('StatisticsSection render - loading:', loading, 'error:', error, 'stats count:', stats.length);

  if (loading) {
    return (
      <section className="py-5" style={{ backgroundColor: colors.bodyBlack, color: colors.white, minHeight: '400px' }}>
        <div className="container">
          <div className="text-center py-5">
            <div className="spinner-border text-light" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading statistics...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-5" style={{ backgroundColor: colors.bodyBlack, color: colors.white, minHeight: '400px' }}>
        <div className="container">
          <div className="text-center py-5">
            <div className="alert alert-danger d-inline-block">
              <h5>Error loading statistics</h5>
              <p className="mb-0">{error}</p>
            </div>
            <br />
            <button 
              className="btn btn-light mt-3"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!stats || stats.length === 0) {
    return (
      <section className="py-5" style={{ backgroundColor: colors.bodyBlack, color: colors.white, minHeight: '400px' }}>
        <div className="container">
          <div className="text-center py-5">
            <h3 className="text-warning">⚠️ No Statistics Available</h3>
            <p className="mt-3">The API returned no statistics data.</p>
            {rawResponse && (
              <details className="mt-3 text-start" style={{ maxWidth: '600px', margin: '20px auto' }}>
                <summary className="btn btn-sm btn-outline-light">Show Raw API Response</summary>
                <pre className="mt-3 p-3 bg-dark text-light text-start" style={{ fontSize: '12px', maxHeight: '300px', overflow: 'auto' }}>
                  {JSON.stringify(rawResponse, null, 2)}
                </pre>
              </details>
            )}
            <button 
              className="btn btn-light mt-3"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  // Helper function to get stat value - tries multiple possible field names
  const getStatValue = (stat) => {
    return stat.count || stat.value || stat.number || stat.amount || stat.total || '0';
  };

  // Helper function to get stat title - tries multiple possible field names
  const getStatTitle = (stat) => {
    return stat.title || stat.name || stat.label || stat.description || stat.text || 'Statistic';
  };

  // Helper function to format stat display value
  const formatStatDisplay = (value) => {
    // Convert to string for comparison
    const stringValue = String(value).toLowerCase().trim();
    
    // Check if the value is "24/7" (case insensitive and allowing for spaces)
    if (stringValue === '24') {
      return '24/7';
    }
    
    // For all other values, add the plus sign
    return `${value}+`;
  };

  return (
    <section 
      className="position-relative overflow-hidden" 
      style={{ backgroundColor: colors.bodyBlack, color: colors.white, minHeight: '400px' }}
      data-section="stats"
    >
      {/* Background Image Overlay */}
      <div className="position-absolute w-100 h-100" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&h=400&fit=crop)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.3
      }}></div>
      
      {/* Content */}
      <div className="container position-relative py-5" style={{zIndex: 2}}>
        <div className="row align-items-center">
          {/* Statistics Grid */}
          <div className="col-lg-6 mb-4 mb-lg-0">
            <div className="row g-4">
              {stats.map((stat, index) => {
                const value = getStatValue(stat);
                const title = getStatTitle(stat);
                const displayValue = formatStatDisplay(value);
                
                return (
                  <div key={stat.id || index} className="col-6">
                    <div className="text-center">
                      <h2 className="display-4 fw-bold mb-2" style={{ color: colors.primary || '#007bff' }}>
                        {displayValue}
                      </h2>
                      <p className="mb-0 fw-bold" style={{ color: 'rgba(255,255,255,0.9)' }}>
                        {title}
                      </p>
                      <div style={{
                        height: '3px', 
                        width: '60px', 
                        margin: '10px auto',
                        backgroundColor: colors.primary || '#007bff'
                      }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Description Card */}
          <div className="col-lg-6">
            <div 
              className="p-4 rounded shadow-lg"
              style={{ 
                backgroundColor: colors.white,
                color: colors.bodyBlack 
              }}
            >
              <h4 className="mb-3" style={{ color: colors.primary || '#007bff' }}>
                Digital Transformation Partner
              </h4>
              <p className="mb-4">
                IDLBAY is dedicated to empowering businesses through cutting-edge IT solutions and services. 
                With a focus on innovation, quality, and client satisfaction, we partner with you to navigate 
                the complexities of the digital landscape and achieve your strategic objectives.
              </p>
              {/* <div className="d-flex justify-content-between align-items-center">
                <span className="badge bg-primary">{stats.length} Statistics</span>
                <small className="text-muted">Live Data</small>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatisticsSection;